import React from 'react';
import styled from 'styled-components';

import { Product } from 'types/graphql-overrides';
import usePaths from 'hooks/use-paths';
import useUI from 'shared/hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import EmptyLoadingState from 'src/components/empty-loading-state';
import Card from 'src/components/product-card-v2/card';
import { CarouselV2 } from 'src/components/carousel-v2/carousel-v2';
import { useQuickAddToCart } from 'src/components/product-carousel/use-quick-add-to-cart';
import { tracker } from 'src/utils/analytics';

import { BuyItAgainOutOfStockCard } from './buy-it-again-out-of-stock-card';
import { BuyItAgainSubstituteModalController } from './buy-it-again-substitute-modal';
import { useBuyItAgainCarousel } from './use-buy-it-again-carousel';

export function BuyItAgainCarousel(): JSX.Element | null {
  const { dispensary } = useDispensary();
  const UI = useUI();
  const { href } = usePaths({ dispensary });

  const hasUserPaths = Boolean(UI.isEmbedded) || Boolean(UI.isStoreFront);
  const ordersPath = hasUserPaths ? `${href}/user/orders` : '/account/orders';

  const { items, loading, handleViewAllClick, viewportVisibility, trackerSource } = useBuyItAgainCarousel();
  const { handleAddToCart } = useQuickAddToCart();

  if (loading) {
    return <EmptyLoadingState isLoading={loading} showBorder={false} page='productCarousel' />;
  }

  if (!items.length) {
    return null;
  }

  const handleProductClick = (product: Product, index: number): void => {
    if (!trackerSource) {
      return;
    }
    tracker.setContext({ activeProductPosition: index });
    tracker.productClicked({ product, position: index, trackerSource });
    UI.activeProductSource = trackerSource;
  };

  const handleQuickAddClick = (product: Product, index: number): void => {
    if (!trackerSource) {
      return;
    }
    tracker.setContext({ activeProductPosition: index });
    tracker.productClicked({ product, position: index, trackerSource });
    UI.activeProductSource = trackerSource;
    void handleAddToCart(product, trackerSource);
  };

  return (
    <>
      <BuyItAgainSubstituteModalController />
      <CarouselV2
        ref={viewportVisibility.ref}
        title='Buy it again'
        href={ordersPath}
        onLinkClick={() => handleViewAllClick(ordersPath)}
        enableLazyRendering
      >
        {items.map((item, index) => {
          if (item.type === 'available') {
            return (
              <Wrapper key={item.product.id} role='group' aria-roledescription='slide' aria-label={item.product.name}>
                <Card
                  data-testid='carousel-product-card'
                  product={item.product}
                  productIndex={index}
                  onClick={() => handleProductClick(item.product, index)}
                  onQuickAddClick={() => handleQuickAddClick(item.product, index)}
                  isCarousel
                />
              </Wrapper>
            );
          }

          return (
            <Wrapper
              key={item.originalProductId}
              role='group'
              aria-roledescription='slide'
              aria-label={item.originalProduct.Name ?? ''}
            >
              <BuyItAgainOutOfStockCard
                originalProduct={item.originalProduct}
                substituteCandidates={item.substituteCandidates}
                trackerSource={trackerSource}
              />
            </Wrapper>
          );
        })}
      </CarouselV2>
    </>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex: 0 0 auto;
`;
